import { combineReducers, configureStore } from '@reduxjs/toolkit'
import { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { createMigrate, persistReducer, persistStore } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import { setupListeners } from '@reduxjs/toolkit/query'
import userReducer from './user/reducer'
import globalReducer from './global/reducer'
import { userApi } from './services/user/user'
import { nftApi } from './services/nft/nft'
import { indexNodeApi } from './services/indexNode/indexNode'
import { communityApi } from './services/community/community'
import { configApi } from './services/config/config'
import { indexNodeRewards } from './services/indexNodeRewards/indexNodeRewards'

/**
 * Migration 11 retires the legacy `metaproContracts` persisted shape
 * (with sales/spacepad/vesting slots) and rebuilds `mprolabContracts`
 * so clients upgrading from older persisted state don't crash on stale keys.
 */
const migrations = {
  11: (state: any) => {
    if (!state || !state.global) return state
    return {
      ...state,
      global: {
        ...state.global,
        mprolabContracts: {
          mproContract: [],
          nodeOwnersContract: [],
          mproReferralContract: [],
          mproStakeRewardContract: [],
          uniswapRouterContract: [],
          usdcContract: [],
          classicStakeContract: [],
          classicDoubleStakeContract: [],
          mproRewardProxyContract: [],
          mgaRegularNodesContract: [],
          lightNodesContract: [],
          regularNodesContract: []
        },
        mprolabContractsLastModifiedDate: null,
        metaproContracts: undefined,
        metaproContractsLastModifiedDate: undefined
      }
    }
  }
}

const persistConfig = {
  key: 'primary',
  storage,
  version: 11,
  migrate: createMigrate(migrations)
}

const persistedReducer = persistReducer(persistConfig, combineReducers({ user: userReducer, global: globalReducer }))

// eslint-disable-next-line import/no-mutable-exports
let store: ReturnType<typeof makeStore>

export function makeStore(preloadedState = undefined) {
  return configureStore({
    reducer: {
      persistedReducer,
      user: userReducer,
      [userApi.reducerPath]: userApi.reducer,
      [nftApi.reducerPath]: nftApi.reducer,
      [indexNodeApi.reducerPath]: indexNodeApi.reducer,
      [configApi.reducerPath]: configApi.reducer,
      [indexNodeRewards.reducerPath]: indexNodeRewards.reducer,
      [communityApi.reducerPath]: communityApi.reducer
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: true,
        serializableCheck: false
      })
        .concat(userApi.middleware)
        .concat(nftApi.middleware)
        .concat(indexNodeApi.middleware)
        .concat(configApi.middleware)
        .concat(indexNodeRewards.middleware)
        .concat(communityApi.middleware),
    devTools: import.meta.env.DEV,
    preloadedState
  })
}

export const initializeStore = (preloadedState = undefined) => {
  let _store = store ?? makeStore(preloadedState)

  if (preloadedState && store) {
    _store = makeStore({
      ...store.getState(),
      ...preloadedState
    })
    store = undefined
  }

  if (typeof window === 'undefined') return _store

  if (!store) {
    store = _store
  }

  return _store
}

store = initializeStore()

setupListeners(store.dispatch)

export type AppDispatch = typeof store.dispatch
export type AppState = ReturnType<typeof store.getState>
export const useAppDispatch = () => useDispatch<AppDispatch>()

export default store

export const persistor = persistStore(store)

export function useStore(initialState) {
  return useMemo(() => initializeStore(initialState), [initialState])
}
